import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Star, Calendar, MapPin, Clock, ShieldCheck, Zap, Lock, Sun, Moon, CircleDot, ArrowUpRight, FileText, Heart, Check, CreditCard, RotateCcw, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { BetaReportPreview } from '../components/reports/BetaReportPreview';
import { PremiumReportPreview } from '../components/reports/PremiumReportPreview';
import { useCart } from '../contexts/CartContext';
import { useLanguage } from '../i18n/LanguageContext';
import { AdSlot } from '../components/ads/AdSlot';
interface FreeChartPageProps {
  onNavigate: (page: string) => void;
}
export function FreeChartPage({
  onNavigate
}: FreeChartPageProps) {
  const {
    t
  } = useLanguage();
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState('natal');
  const [selectedPackage, setSelectedPackage] = useState<'free' | 'pro' | 'package'>('free');
  const [selectedConsultationTier, setSelectedConsultationTier] = useState<'short' | 'standard' | 'extended'>('standard');
  const [selectedBundleSize, setSelectedBundleSize] = useState<20 | 50 | 100>(50);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationComplete, setCalculationComplete] = useState(false);
  const {
    addItem
  } = useCart();
  const [formData, setFormData] = useState({
    p1: {
      name: '',
      date: '',
      time: '',
      city: ''
    },
    p2: {
      name: '',
      date: '',
      time: '',
      city: ''
    },
    year: new Date().getFullYear().toString(),
    period: '3'
  });
  const SERVICES = [{
    id: 'natal',
    title: t('services.natal'),
    desc: t('services.natal.desc'),
    icon: Star,
    color: 'text-indigo-500',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    activeBg: 'bg-indigo-500/10',
    activeBorder: 'border-indigo-500',
    price: 3900
  }, {
    id: 'solar',
    title: t('services.solar'),
    desc: t('services.solar.desc'),
    icon: Sun,
    color: 'text-amber-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    activeBg: 'bg-amber-500/10',
    activeBorder: 'border-amber-500',
    price: 4400
  }, {
    id: 'compatibility',
    title: t('services.compatibility'),
    desc: t('services.compatibility.desc'),
    icon: Heart,
    color: 'text-rose-500',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    activeBg: 'bg-rose-500/10',
    activeBorder: 'border-rose-500',
    price: 4900
  }, {
    id: 'combo',
    title: t('services.combo'),
    desc: t('services.combo.desc'),
    icon: FileText,
    color: 'text-slate-500',
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    activeBg: 'bg-slate-500/10',
    activeBorder: 'border-slate-500',
    price: 6900
  }, {
    id: 'forecast',
    title: t('services.forecast'),
    desc: t('services.forecast.desc'),
    icon: Calendar,
    color: 'text-emerald-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    activeBg: 'bg-emerald-500/10',
    activeBorder: 'border-emerald-500',
    price: 3900
  }, {
    id: 'consultation',
    title: t('services.consultation'),
    desc: t('services.consultation.desc'),
    icon: MessageCircle,
    color: 'text-sky-500',
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    activeBg: 'bg-sky-500/10',
    activeBorder: 'border-sky-500',
    price: 4990
  }];
  const CONSULTATION_TIERS = [{
    id: 'short' as const,
    title: t('chart.consult.short.title'),
    desc: t('chart.consult.short.desc'),
    price: 2990,
    badge: t('chart.consult.short.badge'),
    badgeVariant: 'neutral' as const
  }, {
    id: 'standard' as const,
    title: t('chart.consult.standard.title'),
    desc: t('chart.consult.standard.desc'),
    price: 4990,
    badge: t('chart.consult.standard.badge'),
    badgeVariant: 'pro' as const,
    popular: true
  }, {
    id: 'extended' as const,
    title: t('chart.consult.extended.title'),
    desc: t('chart.consult.extended.desc'),
    price: 7990,
    badge: t('chart.consult.extended.badge'),
    badgeVariant: 'success' as const
  }];
  const STEPS = [{
    id: 1,
    title: t('chart.step.service')
  }, {
    id: 2,
    title: t('chart.step.package')
  }, {
    id: 3,
    title: t('chart.step.data')
  }, {
    id: 4,
    title: t('chart.step.result')
  }];
  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [step]);
  const handleNext = () => {
    if (step < 4) setStep(step + 1);
  };
  const handlePrev = () => {
    if (step > 1) setStep(step - 1);
  };
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedService === 'consultation') {
      const tier = CONSULTATION_TIERS.find(t => t.id === selectedConsultationTier) || CONSULTATION_TIERS[1];
      addItem({
        id: `consultation-${selectedConsultationTier}-${Date.now()}`,
        name: tier.title,
        description: `${tier.desc} • Живой разбор с профессиональным астрологом`,
        price: tier.price,
        type: 'consultation',
        format: 'Видео / Аудио',
        duration: tier.desc.split('•')[0].trim(),
        badges: [{
          label: 'Консультация',
          variant: 'pro'
        }]
      });
      onNavigate('checkout');
      return;
    }
    if (selectedPackage === 'package') {
      const bundlePrice = selectedBundleSize === 20 ? 19900 : selectedBundleSize === 50 ? 44900 : 79900;
      addItem({
        id: `pkg-${selectedBundleSize}-${Date.now()}`,
        name: `Пакет ${selectedBundleSize} отчётов`,
        description: `${selectedBundleSize} Premium-отчётов для любых услуг платформы`,
        price: bundlePrice,
        type: 'package',
        format: 'PDF-отчёты',
        duration: 'Бессрочно',
        badges: [{
          label: 'Пакет отчётов',
          variant: 'success'
        }]
      });
      onNavigate('checkout');
      return;
    }
    setStep(4);
    setIsCalculating(true);
    setCalculationComplete(false);
    // Simulate calculation delay
    setTimeout(() => {
      setIsCalculating(false);
      setCalculationComplete(true);
    }, 2000);
  };
  const resetWizard = () => {
    setStep(1);
    setCalculationComplete(false);
    setIsCalculating(false);
  };
  const currentService = SERVICES.find(s => s.id === selectedService) || SERVICES[0];
  // Mock planet data for the results table
  const planetPositions = [{
    name: t('chart.planet.sun'),
    icon: <Sun className="w-4 h-4 text-amber-500" data-id="element-19549" />,
    sign: t('chart.sign.aries'),
    degree: "15° 24'"
  }, {
    name: t('chart.planet.moon'),
    icon: <Moon className="w-4 h-4 text-slate-400" data-id="element-19550" />,
    sign: t('chart.sign.taurus'),
    degree: "03° 12'"
  }, {
    name: t('chart.planet.mercury'),
    icon: <CircleDot className="w-4 h-4 text-emerald-500" data-id="element-19551" />,
    sign: t('chart.sign.pisces'),
    degree: "28° 45'"
  }, {
    name: t('chart.planet.venus'),
    icon: <Star className="w-4 h-4 text-rose-500" data-id="element-19552" />,
    sign: t('chart.sign.taurus'),
    degree: "10° 05'"
  }, {
    name: t('chart.planet.mars'),
    icon: <ArrowUpRight className="w-4 h-4 text-red-500" data-id="element-19553" />,
    sign: t('chart.sign.gemini'),
    degree: "05° 30'"
  }, {
    name: t('chart.planet.jupiter'),
    icon: <Star className="w-4 h-4 text-indigo-500" data-id="element-19554" />,
    sign: t('chart.sign.sagittarius'),
    degree: "22° 15'"
  }, {
    name: t('chart.planet.saturn'),
    icon: <Star className="w-4 h-4 text-slate-600" data-id="element-19555" />,
    sign: t('chart.sign.capricorn'),
    degree: "14° 50'"
  }];
  const renderStepper = () => <div className="mb-8" data-id="element-19556">
      <div className="flex items-center justify-between relative" data-id="element-19557">
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-slate-100 rounded-full z-0" data-id="element-19558"></div>
        <div className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-indigo-500 rounded-full z-0 transition-all duration-300" style={{
        width: `${(step - 1) / (STEPS.length - 1) * 100}%`
      }} data-id="element-19559"></div>

        {STEPS.map(s => {
        const isActive = s.id === step;
        const isCompleted = s.id < step;
        return <div key={s.id} className="relative z-10 flex flex-col items-center" data-id="element-19560">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${isActive ? 'bg-indigo-600 text-white shadow-md ring-4 ring-indigo-50' : isCompleted ? 'bg-indigo-500 text-white' : 'bg-white text-slate-400 border-2 border-slate-200'}`} data-id="element-19561">
                {isCompleted ? <Check className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-19562" /> : s.id}
              </div>
              <span className={`absolute top-full mt-2 text-[10px] sm:text-xs font-medium whitespace-nowrap ${isActive ? 'text-indigo-900' : isCompleted ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-19563">
                {s.title}
              </span>
            </div>;
      })}
      </div>
    </div>;
  const renderStep1 = () => <motion.div key="step1" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} className="space-y-6" data-id="element-19564">
      <div className="text-center mb-6" data-id="element-19565">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" data-id="element-19566">
          {t('chart.step1.title')}
        </h2>
        <p className="text-slate-500 text-sm sm:text-base" data-id="element-19567">
          {t('chart.step1.subtitle')}
        </p>
      </div>

      <div className="space-y-2 max-w-2xl mx-auto" data-id="element-19568">
        {SERVICES.map(service => {
        const Icon = service.icon;
        const isActive = selectedService === service.id;
        return <button key={service.id} onClick={() => setSelectedService(service.id)} className={`flex items-center gap-3 w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${isActive ? `${service.activeBorder} ${service.activeBg} shadow-sm` : 'border-slate-100 bg-white hover:border-slate-200'}`} data-id="element-19569">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${service.bg}`} data-id="element-19570">
                <Icon className={`w-4.5 h-4.5 ${service.color}`} data-id="element-19571" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-19572">
                <div className="font-semibold text-slate-900 text-sm leading-tight flex items-center gap-2" data-id="element-19573">
                  {service.title}
                  {service.id === 'natal' && <Badge variant="pro" className="text-[9px] px-1.5 py-0" data-id="element-19574">
                      {t('chart.popular')}
                    </Badge>}
                  {service.id === 'consultation' && <Badge variant="success" className="text-[9px] px-1.5 py-0" data-id="element-19575">
                      {t('chart.new')}
                    </Badge>}
                </div>
                <div className="text-xs text-slate-500 truncate mt-0.5" data-id="element-19576">
                  {service.desc}
                </div>
              </div>
              <div className="text-right flex-shrink-0 hidden sm:block" data-id="element-19577">
                <div className="text-xs font-bold text-slate-900" data-id="element-19578">
                  {service.id === 'consultation' ? t('chart.fromPrice') : t('chart.freePremium')}
                </div>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? `${service.activeBorder} ${service.activeBg}` : 'border-slate-300'}`} data-id="element-19579">
                {isActive && <div className={`w-2.5 h-2.5 rounded-full ${service.activeBorder.replace('border-', 'bg-')}`} data-id="element-19580" />}
              </div>
            </button>;
      })}
      </div>

      <div className="flex justify-end pt-6 border-t border-slate-100 mt-8" data-id="element-19581">
        <Button onClick={handleNext} variant="primary" icon={<ArrowRight className="w-4 h-4" data-id="element-19583" />} data-id="element-19582">
          {t('chart.btn.next')}
        </Button>
      </div>
    </motion.div>;
  const renderStep2 = () => {
    const CurrentIcon = currentService.icon;
    const isConsultation = selectedService === 'consultation';
    return <motion.div key="step2" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -20
    }} className="space-y-6" data-id="element-19584">
        <div className="text-center mb-6" data-id="element-19585">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" data-id="element-19586">
            {isConsultation ? t('chart.step2.titleConsultation') : t('chart.step2.titleReport')}
          </h2>
          <p className="text-slate-500 text-sm sm:text-base" data-id="element-19587">
            {t('chart.step2.forService', {
            name: currentService.title
          })}
          </p>
        </div>

        <div className="max-w-2xl mx-auto space-y-6" data-id="element-19588">
          {/* Selected Service Summary */}
          <div className={`flex items-center gap-3 p-4 rounded-xl border border-slate-100 ${currentService.bg} bg-opacity-50`} data-id="element-19589">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-white shadow-sm flex-shrink-0`} data-id="element-19590">
              <CurrentIcon className={`w-5 h-5 ${currentService.color}`} data-id="element-19591" />
            </div>
            <div data-id="element-19592">
              <div className="text-sm font-bold text-slate-900" data-id="element-19593">
                {currentService.title}
              </div>
              <div className="text-xs text-slate-500" data-id="element-19594">
                {currentService.desc}
              </div>
            </div>
          </div>

          {/* Tier Selection */}
          <div className="space-y-3" data-id="element-19595">
            {isConsultation ? <>
                {CONSULTATION_TIERS.map(tier => {
              const isActive = selectedConsultationTier === tier.id;
              return <button key={tier.id} onClick={() => setSelectedConsultationTier(tier.id)} className={`flex items-center gap-4 w-full text-left p-4 rounded-xl border-2 transition-all ${isActive ? 'border-sky-500 bg-sky-50/30 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-19596">
                      <div className="flex-1 min-w-0" data-id="element-19597">
                        <div className="flex items-center gap-2 mb-1" data-id="element-19598">
                          <span className="font-bold text-slate-900" data-id="element-19599">
                            {tier.title}
                          </span>
                          <Badge variant={tier.badgeVariant} className="text-[10px]" data-id="element-19600">
                            {tier.badge}
                          </Badge>
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-19601">
                          {tier.desc}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2" data-id="element-19602">
                        <div className="text-lg font-bold text-slate-900" data-id="element-19603">
                          {tier.price.toLocaleString('ru-RU')} ₽
                        </div>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-2 transition-colors ${isActive ? 'border-sky-500 bg-sky-500' : 'border-slate-300'}`} data-id="element-19604">
                        {isActive && <div className="w-2 h-2 rounded-full bg-white" data-id="element-19605" />}
                      </div>
                    </button>;
            })}
              </> : <>
                {/* Free Tier */}
                <button onClick={() => setSelectedPackage('free')} className={`flex items-center gap-4 w-full text-left p-4 rounded-xl border-2 transition-all ${selectedPackage === 'free' ? 'border-indigo-500 bg-indigo-50/30 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-19606">
                  <div className="flex-1 min-w-0" data-id="element-19607">
                    <div className="flex items-center gap-2 mb-1" data-id="element-19608">
                      <span className="font-bold text-slate-900" data-id="element-19609">
                        {t('chart.tier.free.title')}
                      </span>
                      <Badge variant="neutral" className="text-[10px]" data-id="element-19610">
                        {t('chart.tier.free.badge')}
                      </Badge>
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-19611">
                      {t('chart.tier.free.desc')}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2" data-id="element-19612">
                    <div className="text-lg font-bold text-slate-900" data-id="element-19613">0 ₽</div>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-2 transition-colors ${selectedPackage === 'free' ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300'}`} data-id="element-19614">
                    {selectedPackage === 'free' && <div className="w-2 h-2 rounded-full bg-white" data-id="element-19615" />}
                  </div>
                </button>

                {/* Pro Tier */}
                <button onClick={() => setSelectedPackage('pro')} className={`flex items-center gap-4 w-full text-left p-4 rounded-xl border-2 transition-all ${selectedPackage === 'pro' ? 'border-indigo-500 bg-indigo-50/30 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-19616">
                  <div className="flex-1 min-w-0" data-id="element-19617">
                    <div className="flex items-center gap-2 mb-1" data-id="element-19618">
                      <span className="font-bold text-slate-900" data-id="element-19619">
                        {t('chart.tier.pro.title')}
                      </span>
                      <Badge variant="pro" className="text-[10px]" data-id="element-19620">
                        {t('chart.tier.pro.badge')}
                      </Badge>
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-19621">
                      {t('chart.tier.pro.desc')}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2" data-id="element-19622">
                    <div className="text-lg font-bold text-slate-900" data-id="element-19623">
                      {currentService.price.toLocaleString('ru-RU')} ₽
                    </div>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-2 transition-colors ${selectedPackage === 'pro' ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300'}`} data-id="element-19624">
                    {selectedPackage === 'pro' && <div className="w-2 h-2 rounded-full bg-white" data-id="element-19625" />}
                  </div>
                </button>

                {/* Package Tier */}
                <div className={`rounded-xl border-2 transition-all overflow-hidden ${selectedPackage === 'package' ? 'border-violet-500 bg-violet-50/30 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-19626">
                  <button onClick={() => setSelectedPackage('package')} className="flex items-center gap-4 w-full text-left p-4" data-id="element-19627">
                    <div className="flex-1 min-w-0" data-id="element-19628">
                      <div className="flex items-center gap-2 mb-1" data-id="element-19629">
                        <span className="font-bold text-slate-900" data-id="element-19630">
                          {t('chart.tier.package.title')}
                        </span>
                        <Badge variant="success" className="text-[10px]" data-id="element-19631">
                          {t('chart.tier.package.badge')}
                        </Badge>
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-19632">
                        {t('chart.tier.package.desc')}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2" data-id="element-19633">
                      <div className="text-sm font-bold text-violet-600" data-id="element-19634">
                        {t('chart.tier.package.fromPrice')}
                      </div>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-2 transition-colors ${selectedPackage === 'package' ? 'border-violet-500 bg-violet-500' : 'border-slate-300'}`} data-id="element-19635">
                      {selectedPackage === 'package' && <div className="w-2 h-2 rounded-full bg-white" data-id="element-19636" />}
                    </div>
                  </button>

                  {/* Expandable Package Options */}
                  <AnimatePresence data-id="element-19637">
                    {selectedPackage === 'package' && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="border-t border-violet-100 bg-white/50" data-id="element-19638">
                        <div className="p-4 space-y-2" data-id="element-19639">
                          {[{
                      size: 20 as const,
                      price: 19900,
                      perItem: 995
                    }, {
                      size: 50 as const,
                      price: 44900,
                      perItem: 898,
                      popular: true
                    }, {
                      size: 100 as const,
                      price: 79900,
                      perItem: 799
                    }].map(bundle => <button key={bundle.size} onClick={() => setSelectedBundleSize(bundle.size)} className={`flex items-center justify-between w-full p-3 rounded-lg border transition-all ${selectedBundleSize === bundle.size ? 'border-violet-500 bg-violet-100/50' : 'border-slate-200 bg-white hover:border-violet-300'}`} data-id="element-19640">
                              <div className="flex items-center gap-3" data-id="element-19641">
                                <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${selectedBundleSize === bundle.size ? 'border-violet-500' : 'border-slate-300'}`} data-id="element-19642">
                                  {selectedBundleSize === bundle.size && <div className="w-2 h-2 rounded-full bg-violet-500" data-id="element-19643" />}
                                </div>
                                <div className="flex items-center gap-2" data-id="element-19644">
                                  <span className="font-bold text-slate-900 text-sm" data-id="element-19645">
                                    {t('chart.bundle.reports', {
                              count: bundle.size.toString()
                            })}
                                  </span>
                                  {bundle.popular && <span className="text-[9px] font-bold text-white bg-violet-500 px-1.5 py-0.5 rounded-full" data-id="element-19646">
                                      {t('chart.bundle.hit')}
                                    </span>}
                                </div>
                              </div>
                              <div className="text-right" data-id="element-19647">
                                <div className="text-sm font-bold text-slate-900" data-id="element-19648">
                                  {bundle.price.toLocaleString('ru-RU')} ₽
                                </div>
                                <div className="text-[10px] text-violet-600" data-id="element-19649">
                                  {bundle.perItem.toLocaleString('ru-RU')} ₽/шт
                                </div>
                              </div>
                            </button>)}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </>}
          </div>
        </div>

        <div className="flex justify-between pt-6 border-t border-slate-100 mt-8 max-w-2xl mx-auto" data-id="element-19650">
          <Button onClick={handlePrev} variant="outline" icon={<ArrowLeft className="w-4 h-4" data-id="element-19652" />} data-id="element-19651">
            {t('chart.btn.back')}
          </Button>
          <Button onClick={handleNext} variant="primary" icon={<ArrowRight className="w-4 h-4" data-id="element-19654" />} data-id="element-19653">
            {t('chart.btn.next')}
          </Button>
        </div>
      </motion.div>;
  };
  const renderPersonFields = (personKey: 'p1' | 'p2', title?: string) => <div className="space-y-4 bg-slate-50 p-4 sm:p-6 rounded-2xl border border-slate-100" data-id="element-19655">
      {title && <h3 className="font-bold text-slate-900 mb-2" data-id="element-19656">{title}</h3>}
      <Input label={t('chart.field.name')} placeholder={t('chart.field.namePlaceholder')} value={formData[personKey].name} onChange={e => setFormData({
      ...formData,
      [personKey]: {
        ...formData[personKey],
        name: e.target.value
      }
    })} data-id="element-19657" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-19658">
        <Input label={t('chart.field.date')} type="date" required value={formData[personKey].date} onChange={e => setFormData({
        ...formData,
        [personKey]: {
          ...formData[personKey],
          date: e.target.value
        }
      })} data-id="element-19659" />
        <div data-id="element-19660">
          <Input label={t('chart.field.time')} type="time" required value={formData[personKey].time} onChange={e => setFormData({
          ...formData,
          [personKey]: {
            ...formData[personKey],
            time: e.target.value
          }
        })} data-id="element-19661" />
          <p className="text-[10px] sm:text-xs text-slate-500 mt-1" data-id="element-19662">
            {t('chart.field.timeHint')}
          </p>
        </div>
      </div>
      <Input label={t('chart.field.city')} placeholder={t('chart.field.cityPlaceholder')} required icon={<MapPin className="w-4 h-4" data-id="element-19664" />} value={formData[personKey].city} onChange={e => setFormData({
      ...formData,
      [personKey]: {
        ...formData[personKey],
        city: e.target.value
      }
    })} data-id="element-19663" />
    </div>;
  const renderStep3 = () => {
    const CurrentIcon = currentService.icon;
    return <motion.div key="step3" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -20
    }} className="space-y-6" data-id="element-19665">
        <div className="text-center mb-6" data-id="element-19666">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" data-id="element-19667">
            {t('chart.step3.title')}
          </h2>
          <p className="text-slate-500 text-sm sm:text-base" data-id="element-19668">
            {t('chart.step3.subtitle', {
            name: currentService.title
          })}
          </p>
        </div>

        <div className="max-w-2xl mx-auto mb-6" data-id="element-19669">
          <div className="flex items-center justify-between p-3 sm:p-4 rounded-xl bg-indigo-50/50 border border-indigo-100" data-id="element-19670">
            <div className="flex items-center gap-3" data-id="element-19671">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-white shadow-sm flex-shrink-0 ${currentService.color}`} data-id="element-19672">
                <CurrentIcon className="w-4 h-4" data-id="element-19673" />
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2" data-id="element-19674">
                <span className="text-sm font-bold text-slate-900" data-id="element-19675">
                  {currentService.title}
                </span>
                <span className="hidden sm:inline text-slate-300" data-id="element-19676">•</span>
                <span className="text-xs font-medium text-slate-600" data-id="element-19677">
                  {selectedService === 'consultation' ? CONSULTATION_TIERS.find(t => t.id === selectedConsultationTier)?.title : selectedPackage === 'free' ? t('chart.tier.free.title') : selectedPackage === 'pro' ? t('chart.tier.pro.title') : t('chart.bundle.reports', {
                  count: selectedBundleSize.toString()
                })}
                </span>
              </div>
            </div>
            <div className="text-right" data-id="element-19678">
              <div className="text-sm font-bold text-indigo-600" data-id="element-19679">
                {selectedService === 'consultation' ? `${(CONSULTATION_TIERS.find(t => t.id === selectedConsultationTier)?.price || 4990).toLocaleString('ru-RU')} ₽` : selectedPackage === 'free' ? '0 ₽' : selectedPackage === 'pro' ? `${currentService.price.toLocaleString('ru-RU')} ₽` : `${(selectedBundleSize === 20 ? 19900 : selectedBundleSize === 50 ? 44900 : 79900).toLocaleString('ru-RU')} ₽`}
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleCalculate} className="space-y-6 max-w-2xl mx-auto" data-id="element-19680">
          {selectedService === 'compatibility' ? <div className="space-y-6" data-id="element-19681">
              {renderPersonFields('p1', t('chart.field.person1'))}
              {renderPersonFields('p2', t('chart.field.person2'))}
            </div> : <div className="space-y-6" data-id="element-19682">
              {renderPersonFields('p1')}

              {selectedService === 'solar' && <div className="bg-amber-50/50 p-4 sm:p-6 rounded-2xl border border-amber-100" data-id="element-19683">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-19684">
                    {t('chart.field.forecastYear')}
                  </label>
                  <select className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" value={formData.year} onChange={e => setFormData({
              ...formData,
              year: e.target.value
            })} data-id="element-19685">
                    {[0, 1, 2, 3].map(offset => {
                const year = new Date().getFullYear() + offset;
                return <option key={year} value={year} data-id="element-19686">
                          {year}
                        </option>;
              })}
                  </select>
                </div>}

              {selectedService === 'forecast' && <div className="bg-emerald-50/50 p-4 sm:p-6 rounded-2xl border border-emerald-100" data-id="element-19687">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-19688">
                    {t('chart.field.forecastPeriod')}
                  </label>
                  <select className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" value={formData.period} onChange={e => setFormData({
              ...formData,
              period: e.target.value
            })} data-id="element-19689">
                    <option value="3" data-id="element-19690">{t('chart.period.3months')}</option>
                    <option value="6" data-id="element-19691">{t('chart.period.6months')}</option>
                    <option value="12" data-id="element-19692">{t('chart.period.1year')}</option>
                  </select>
                </div>}
            </div>}

          <div className="flex justify-between pt-6 border-t border-slate-100 mt-8" data-id="element-19693">
            <Button type="button" onClick={handlePrev} variant="outline" icon={<ArrowLeft className="w-4 h-4" data-id="element-19695" />} data-id="element-19694">
              {t('chart.btn.back')}
            </Button>
            <Button type="submit" variant="primary" icon={selectedService === 'consultation' ? <ArrowRight className="w-4 h-4" data-id="element-19697" /> : <Zap className="w-4 h-4" data-id="element-19698" />} data-id="element-19696">
              {selectedService === 'consultation' ? t('chart.btn.book') : t('chart.btn.calculate')}
            </Button>
          </div>
        </form>
      </motion.div>;
  };
  const renderStep4 = () => {
    if (isCalculating) {
      return <motion.div key="calculating" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="flex flex-col items-center justify-center py-20 text-center space-y-6" data-id="element-19699">
          <div className="relative w-24 h-24" data-id="element-19700">
            <div className="absolute inset-0 border-4 border-slate-100 rounded-full" data-id="element-19701"></div>
            <div className="absolute inset-0 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin" data-id="element-19702"></div>
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-19703">
              <currentService.icon className={`w-8 h-8 ${currentService.color}`} data-id="element-19704" />
            </div>
          </div>
          <div data-id="element-19705">
            <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-19706">
              {t('chart.calculating.title')}
            </h2>
            <p className="text-slate-500" data-id="element-19707">{t('chart.calculating.subtitle')}</p>
          </div>
        </motion.div>;
    }
    return <motion.div key="result" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-6" data-id="element-19708">
        {/* New calculation button */}
        <div className="flex items-center justify-between" data-id="element-19709">
          <div data-id="element-19710">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-19711">
              {selectedPackage === 'pro' ? t('chart.result.fullReport') : t('chart.result.basicReport')}
              : {currentService.title}
            </h2>
            <p className="text-sm text-slate-500 mt-1" data-id="element-19712">
              {formData.p1.name && `${formData.p1.name} • `}
              {formData.p1.date || '01.01.1990'} •{' '}
              {formData.p1.city || 'Москва'}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={resetWizard} icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-19714" />} data-id="element-19713">
            {t('chart.result.newCalc')}
          </Button>
        </div>

        {/* Render the appropriate report preview */}
        {selectedPackage === 'free' ? <>
            <Card padding="md" className="border-slate-200 mb-8" data-id="element-19715">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-19716">
                <Star className="w-5 h-5 text-indigo-500" data-id="element-19717" />
                {t('chart.result.planets')}
              </h3>
              <div className="overflow-hidden rounded-xl border border-slate-100" data-id="element-19718">
                <table className="w-full text-sm text-left" data-id="element-19719">
                  <thead className="bg-slate-50 text-slate-500 font-medium" data-id="element-19720">
                    <tr data-id="element-19721">
                      <th className="px-4 py-3" data-id="element-19722">{t('chart.result.planet')}</th>
                      <th className="px-4 py-3" data-id="element-19723">{t('chart.result.sign')}</th>
                      <th className="px-4 py-3 text-right" data-id="element-19724">
                        {t('chart.result.degree')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-19725">
                    {planetPositions.map((planet, i) => <tr key={i} className="hover:bg-slate-50/50 transition-colors" data-id="element-19726">
                        <td className="px-4 py-3 flex items-center gap-2 font-medium text-slate-900" data-id="element-19727">
                          {planet.icon}
                          {planet.name}
                        </td>
                        <td className="px-4 py-3 text-slate-600" data-id="element-19728">
                          {planet.sign}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-500 font-mono text-xs" data-id="element-19729">
                          {planet.degree}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </Card>

            <div className="flex items-center gap-4 mb-8" data-id="element-19730">
              <div className="h-px bg-slate-200 flex-1" data-id="element-19731" />
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-19732">
                {t('chart.result.briefInterpretation')}
              </span>
              <div className="h-px bg-slate-200 flex-1" data-id="element-19733" />
            </div>

            <BetaReportPreview serviceType={selectedService as 'natal' | 'solar' | 'compatibility' | 'combo' | 'forecast'} onNavigate={onNavigate} data-id="element-19734" />

            <div className="mt-8 space-y-4" data-id="element-19735">
              <Card padding="lg" className="bg-gradient-to-br from-indigo-900 to-slate-900 border-indigo-800 text-white shadow-xl shadow-indigo-900/20" data-id="element-19736">
                <div className="flex flex-col sm:flex-row items-center gap-6" data-id="element-19737">
                  <div className="flex-1 text-center sm:text-left" data-id="element-19738">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-500/20 text-indigo-300 text-xs font-bold mb-3 border border-indigo-500/30" data-id="element-19739">
                      <Sparkles className="w-3.5 h-3.5" data-id="element-19740" /> Premium
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold mb-2" data-id="element-19741">
                      {t('chart.result.wantFull')}
                    </h3>
                    <p className="text-indigo-200 text-sm leading-relaxed" data-id="element-19742">
                      {t('chart.result.fullDesc')}
                    </p>
                  </div>
                  <div className="w-full sm:w-auto flex-shrink-0" data-id="element-19743">
                    <Button variant="primary" size="lg" fullWidth className="bg-indigo-500 hover:bg-indigo-400 text-white shadow-lg shadow-indigo-500/25" onClick={() => {
                  const priceMap: Record<string, number> = {
                    natal: 3900,
                    solar: 3900,
                    compatibility: 4900,
                    combo: 6900,
                    forecast: 3900
                  };
                  addItem({
                    id: `premium-${selectedService}-${Date.now()}`,
                    name: `Premium отчёт: ${currentService.title}`,
                    description: currentService.desc,
                    price: priceMap[selectedService] || 3900,
                    type: 'report',
                    format: 'PDF-отчёт',
                    badges: [{
                      label: 'Premium',
                      variant: 'pro'
                    }]
                  });
                  onNavigate('checkout');
                }} data-id="element-19744">
                      {t('chart.result.getPremium')}
                    </Button>
                  </div>
                </div>
              </Card>

              <Card padding="md" className="border-slate-200 bg-slate-50 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-19745">
                <div className="flex items-center gap-3" data-id="element-19746">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm text-sky-500" data-id="element-19747">
                    <MessageCircle className="w-5 h-5" data-id="element-19748" />
                  </div>
                  <div data-id="element-19749">
                    <div className="font-bold text-slate-900 text-sm" data-id="element-19750">
                      {t('chart.result.questions')}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-19751">
                      {t('chart.result.consultDesc')}
                    </div>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="w-full sm:w-auto bg-white" onClick={() => onNavigate('service-consultation')} data-id="element-19752">
                  {t('chart.result.chooseAstrologer')}
                </Button>
              </Card>
            </div>
          </> : <PremiumReportPreview serviceType={selectedService as 'natal' | 'solar' | 'compatibility' | 'combo' | 'forecast'} data-id="element-19753" />}
      </motion.div>;
  };
  return <div className="w-full bg-slate-50 min-h-screen pb-24" data-id="element-19754">
      <div className="bg-white border-b border-slate-200 pt-8 pb-12" data-id="element-19755">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-19756">
          <Breadcrumbs items={[{
          label: t('nav.services'),
          onClick: () => onNavigate('home')
        }, {
          label: t('chart.breadcrumb'),
          onClick: resetWizard
        }]} className="mb-8" data-id="element-19757" />

          <div className="text-center mb-10" data-id="element-19758">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4" data-id="element-19759">
              {t('chart.title')}
            </h1>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto mb-6" data-id="element-19760">
              {t('chart.subtitle')}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6" data-id="element-19761">
              <div className="flex items-center gap-1.5 text-sm font-medium text-slate-600 bg-slate-100/80 px-3 py-1.5 rounded-full" data-id="element-19762">
                <Star className="w-4 h-4 text-amber-500 fill-amber-500" data-id="element-19763" />
                {t('chart.rating')}
              </div>
              <div className="flex items-center gap-1.5 text-sm font-medium text-slate-600 bg-slate-100/80 px-3 py-1.5 rounded-full" data-id="element-19764">
                <Zap className="w-4 h-4 text-indigo-500" data-id="element-19765" />
                {t('chart.calculations')}
              </div>
              <div className="flex items-center gap-1.5 text-sm font-medium text-slate-600 bg-slate-100/80 px-3 py-1.5 rounded-full" data-id="element-19766">
                <ShieldCheck className="w-4 h-4 text-emerald-500" data-id="element-19767" />
                {t('chart.freeBeta')}
              </div>
            </div>
          </div>

          {/* Stepper only visible during steps 1-3 */}
          {step < 4 && renderStepper()}
        </div>
      </div>

      <div className={`mx-auto px-4 sm:px-6 lg:px-8 mt-8 sm:mt-12 ${step === 4 ? 'max-w-7xl' : 'max-w-3xl'}`} data-id="element-19768">
        <AnimatePresence mode="wait" data-id="element-19769">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && <div className="space-y-8" data-id="element-19770">
              {renderStep4()}
              <AdSlot slotId="SLOT-08" onNavigate={onNavigate} data-id="element-19771" />
            </div>}
        </AnimatePresence>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24" data-id="element-19772">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6" data-id="element-19773">
          <Card padding="md" className="bg-white border-slate-100 text-center" data-id="element-19774">
            <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center mx-auto mb-4" data-id="element-19775">
              <ShieldCheck className="w-6 h-6 text-indigo-500" data-id="element-19776" />
            </div>
            <h3 className="font-bold text-slate-900 mb-2" data-id="element-19777">
              {t('chart.trust.accurate.title')}
            </h3>
            <p className="text-sm text-slate-500" data-id="element-19778">
              {t('chart.trust.accurate.desc')}
            </p>
          </Card>
          <Card padding="md" className="bg-white border-slate-100 text-center" data-id="element-19779">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4" data-id="element-19780">
              <Lock className="w-6 h-6 text-emerald-500" data-id="element-19781" />
            </div>
            <h3 className="font-bold text-slate-900 mb-2" data-id="element-19782">
              {t('chart.trust.privacy.title')}
            </h3>
            <p className="text-sm text-slate-500" data-id="element-19783">
              {t('chart.trust.privacy.desc')}
            </p>
          </Card>
          <Card padding="md" className="bg-white border-slate-100 text-center" data-id="element-19784">
            <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4" data-id="element-19785">
              <Zap className="w-6 h-6 text-amber-500" data-id="element-19786" />
            </div>
            <h3 className="font-bold text-slate-900 mb-2" data-id="element-19787">
              {t('chart.trust.instant.title')}
            </h3>
            <p className="text-sm text-slate-500" data-id="element-19788">
              {t('chart.trust.instant.desc')}
            </p>
          </Card>
        </div>
      </div>
    </div>;
}